/**
 * Buybox lineup selector.
 *
 * Every product's regions (image, title, features, and the Subscribe & Save +
 * buy-form panel) are pre-rendered server-side and toggled client-side. The
 * thumbnails are an ARIA tablist: each thumb is a tab that controls its product's
 * purchase panel (tabpanel). Selecting a tab (click, arrow keys, or the prev/next
 * buttons) shows the matching product's regions, hides the rest, and announces the
 * change via a polite live region. No fetching — each product comes from its block.
 */
class MiniBuyboxComponent extends HTMLElement {
  /** @type {HTMLElement[]} */
  #thumbs = [];
  /** @type {HTMLElement | null} */
  #status = null;

  connectedCallback() {
    this.#thumbs = Array.from(this.querySelectorAll('[data-mini-buybox-thumb]'));
    this.#status = this.querySelector('[data-mini-buybox-status]');

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('change', this.#handleVariantChange);
  }

  /**
   * Variant (flavor/size) selection within a product panel. Each eligible variant's
   * purchase block is pre-rendered; picking a variant just shows its block and hides
   * the others in the same panel. No fetching — the block carries the right variant's
   * subscription options, price and add-to-cart form.
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.dataset.miniBuyboxVariant === undefined) return;

    const panel = input.closest('[data-mini-buybox-panel]');
    if (!panel) return;

    const variantId = input.value;
    panel.querySelectorAll('[data-mini-buybox-variant-panel]').forEach((block) => {
      if (block instanceof HTMLElement) block.toggleAttribute('hidden', block.dataset.variantId !== variantId);
    });

    // Keep the product title's "(N servings)" suffix in sync with the chosen variant.
    const productId = panel.dataset.productId;
    const servingLabel = input.dataset.servingLabel ?? '';
    const titleServing = this.querySelector(
      `[data-mini-buybox-title][data-product-id="${productId}"] [data-mini-buybox-title-serving]`
    );
    if (titleServing) titleServing.textContent = servingLabel ? ` (${servingLabel})` : '';
  };

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const thumb = target.closest('[data-mini-buybox-thumb]');
    if (thumb instanceof HTMLElement && this.contains(thumb)) {
      this.#select(this.#thumbs.indexOf(thumb), false);
      return;
    }
    if (target.closest('[data-mini-buybox-prev]')) this.#step(-1);
    else if (target.closest('[data-mini-buybox-next]')) this.#step(1);
  };

  /**
   * Tablist keyboard support (APG): arrows move + select, Home/End jump to ends.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const thumb = target.closest('[data-mini-buybox-thumb]');
    if (!(thumb instanceof HTMLElement) || !this.contains(thumb)) return;

    const count = this.#thumbs.length;
    const current = this.#thumbs.indexOf(thumb);
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.#select(next, true);
  };

  /** @param {number} direction -1 | 1, wraps around at the ends. */
  #step(direction) {
    const count = this.#thumbs.length;
    if (count < 2) return;
    const current = this.#thumbs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    const base = current < 0 ? 0 : current;
    this.#select((base + direction + count) % count, false);
  }

  /**
   * @param {number} index
   * @param {boolean} focusTab Move keyboard focus to the selected tab (true for keyboard nav).
   */
  #select(index, focusTab) {
    const thumb = this.#thumbs[index];
    if (!(thumb instanceof HTMLElement)) return;
    const productId = thumb.dataset.productId;
    if (!productId) return;

    // Roving tabindex: only the selected tab is in the tab order.
    this.#thumbs.forEach((t) => {
      const selected = t === thumb;
      t.setAttribute('aria-selected', String(selected));
      t.tabIndex = selected ? 0 : -1;
    });
    thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (focusTab) thumb.focus();

    this.#toggle('[data-mini-buybox-media]', productId);
    this.#toggle('[data-mini-buybox-title]', productId);
    this.#toggle('[data-mini-buybox-features]', productId);
    this.#toggle('[data-mini-buybox-panel]', productId);

    this.#announce(thumb.getAttribute('aria-label'));
  }

  /**
   * @param {string} selector
   * @param {string} productId
   */
  #toggle(selector, productId) {
    this.querySelectorAll(selector).forEach((el) => {
      if (el instanceof HTMLElement) el.toggleAttribute('hidden', el.dataset.productId !== productId);
    });
  }

  /** @param {string | null} label */
  #announce(label) {
    if (this.#status && label) this.#status.textContent = label;
  }
}

if (!customElements.get('mini-buybox-component')) {
  customElements.define('mini-buybox-component', MiniBuyboxComponent);
}
